import castArray from "lodash/castArray";

import { getItemAsync } from "actions/items";

import isEditorActive from "editor/utils/isEditorActive";
import { setActiveEditor } from "editor/actions/status";
import { SECTIONS } from "config/constants";
import { RUBRICS, TEMPLATES } from "config/constants/editor";
import validate from "editor/utils/validate";
import sanitizeSlug from "utils/sanitizeSlug";
import getHydratedDraftInstance from "editor/utils/getHydratedDraftInstance";

export const UPDATE_FORM = "UPDATE_FORM";
export const UPDATE_FORM_AT = "UPDATE_FORM_AT";
export const INITIALIZE_FORM = "INITIALIZE_FORM";
export const VALIDATE_FORM_SUCCEEDED = "VALIDATE_FORM_SUCCEEDED";
export const VALIDATE_FORM_FAILED = "VALIDATE_FORM_FAILED";

export const updateForm = payload => {
  return {
    type: UPDATE_FORM,
    payload,
  };
};

export const updateFormAt = (path, value) => {
  return {
    type: UPDATE_FORM_AT,
    payload: {
      path,
      value,
    },
  };
};

export const initializeForm = payload => {
  return {
    type: INITIALIZE_FORM,
    payload,
  };
};

export const updateEditor = (editorKey, isInlineEditor = false) => {
  return (dispatch, getState) => {
    return editorState => {
      const {
        editor: {
          status: { activeEditor },
        },
      } = getState();
      if (editorKey !== activeEditor.name && isEditorActive(editorState)) {
        dispatch(setActiveEditor(editorKey, isInlineEditor));
      }

      dispatch(updateFormAt(editorKey, editorState));
    };
  };
};

export const updateActiveEditor = editorState => {
  return (dispatch, getState) => {
    const {
      editor: {
        status: { activeEditor },
      },
    } = getState();

    dispatch(updateFormAt(activeEditor.name, editorState));
  };
};

export const updateInput = e => {
  return {
    type: UPDATE_FORM,
    payload: {
      [e.target.name]: e.target.value,
    },
  };
};

/**
 * Bind an onChange function to a specific form key in the render instance.
 * @param {string|Array} fields a field or array of fields in the form key. One
 *    use for an array of fields is to have one field trigger changes in
 *    another field
 * @param {Function} callback
 */
export const onChange = (fields, callback) => dispatch => value => {
  for (const field of castArray(fields)) {
    if (value && value.target) {
      dispatch(updateFormAt(field, value.target.value));
    } else {
      dispatch(updateFormAt(field, value));
    }
  }

  if (callback) {
    callback();
  }
};

export const onChangeCheckbox = (
  field,
  check = value => value,
  checkedValue = true,
  uncheckedValue = false
) => (dispatch, getState) => () => {
  if (check(getState().editor.form[field])) {
    dispatch(updateFormAt(field, uncheckedValue));
  } else {
    dispatch(updateFormAt(field, checkedValue));
  }
};

// Separate this out from onChangeSlug to let natural typing happen
// without ending the slug with a space-> dash conversion sometimes
export const onBlurSlug = value => dispatch => {
  dispatch(
    updateFormAt(
      "slug",
      sanitizeSlug(value.target.value, { removeTrailingDashes: true })
    )
  );
};

export const onChangeSlug = value => dispatch => {
  dispatch(updateFormAt("slug", sanitizeSlug(value.target.value)));
  dispatch(updateFormAt("slug_is_user_modified", true));
};

/**
 * Given an article, process it for handling in the form.
 * @param {string} id story ID
 */
export const hydrateFormUtils = story => {
  const state = {
    errors: {},
  };

  for (let key in story) {
    switch (key) {
      case "blocks": {
        state.content = getHydratedDraftInstance(story.blocks);
        break;
      }

      case "intro":
      case "outro": {
        state[key] = getHydratedDraftInstance(story[key]);
        break;
      }

      case "primary_image": {
        state.primary_image = story.primary_image && { ...story.primary_image };

        if (story.primary_image && story.primary_image.caption) {
          state.primary_image.caption = getHydratedDraftInstance(
            story.primary_image.caption
          );
        }
        break;
      }

      case "social_image": {
        state.social_image = story.social_image;
        break;
      }

      case "sections": {
        const topStorySectionIndex = story.sections.findIndex(
          section => section.name === SECTIONS.TOP.name
        );
        state.sections = story.sections && [...story.sections];
        if (topStorySectionIndex >= 0) {
          state.isTopStory = true;
          state.sections.splice(topStorySectionIndex, 1);
        } else {
          state.isTopStory = false;
        }
        break;
      }

      // Populate "featured for subtopics" list from the subtopics
      case "subtopics": {
        state.subtopics = story.subtopics;
        state.featured_subtopics = story.subtopics.filter(subtopic => {
          return subtopic.featured;
        });
        break;
      }

      case "rubric": {
        if (story.rubric === null) {
          state.rubric = RUBRICS.DEFAULT;
        } else {
          state.rubric = story.rubric;
        }
        break;
      }

      case "template": {
        if (story.template === null) {
          state.template = TEMPLATES.DEFAULT;
        } else {
          state.template = story.template;
        }
        break;
      }

      // Chunks are their own crazy thing.
      case "chunks": {
        state.chunks = story.chunks.map(chunk => {
          const newChunk = {};

          for (let key in chunk) {
            switch (key) {
              case "blocks": {
                newChunk[key] = getHydratedDraftInstance(chunk[key]);
                break;
              }

              case "primary_image":
                newChunk.primary_image = chunk.primary_image && {
                  ...chunk.primary_image,
                };

                if (chunk.primary_image && chunk.primary_image.caption) {
                  newChunk.primary_image.caption = getHydratedDraftInstance(
                    chunk.primary_image.caption
                  );
                }
                break;

              default:
                newChunk[key] = chunk[key];
            }
          }
          return newChunk;
        });
        break;
      }

      // Slug, template, and lock break if we pass them to the API.
      case "lock":
      case "isTopStory":
        break;

      default:
        state[key] = story[key];
    }
  }

  return state;
};

/**
 * Given an article ID, fetch the article, and populate the form.
 * @param {string} id story ID
 */
export const hydrateForm = id => {
  return dispatch => {
    return dispatch(getItemAsync(id)).then(action => {
      const story = action.payload;
      dispatch(initializeForm(hydrateFormUtils(story)));
    });
  };
};

/**
 * Validate a form inside an async action
 * @param {Object} formCandidate - Would this be a valid form, if we were to save it?
 * @returns {Promise}
 */
export const validateFormAsync = (formCandidate, publishAsBreaking = false) => {
  return (dispatch, getState) => {
    const {
      editor: {
        status: {
          editorType: { name },
        },
      },
    } = getState();
    const validationErrs = validate(
      formCandidate,
      name === "newsletters",
      publishAsBreaking
    );

    if (validationErrs) {
      dispatch({ type: VALIDATE_FORM_FAILED, payload: validationErrs });
      return Promise.reject(validationErrs);
    } else {
      dispatch({ type: VALIDATE_FORM_SUCCEEDED });
      return Promise.resolve();
    }
  };
};
